import { ReceiptPaymentMethods } from 'components/receipt-payment-methods';
import { getReceiptDate } from 'utils/receipt-date';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { List } from 'components/list';
import { formatDate } from 'utils/date';
import { Badge } from 'components/badge';
import { Avatar } from 'components/avatar';
import { Button } from 'components/button';
import { Config, AppRoutes } from 'config';
import { ListCard } from 'components/list-card';
import { InfoBlock } from 'components/info-block';
import { TotalCart } from 'components/total-cart';
import { ListHeader } from 'components/list-header';
import { ErrorMessage } from 'components/error-message';
import { isMobile, useSelectedCloverDevice } from 'hooks';
import { useTokenEcommerceQuery } from 'store/api/clover';
import { ExchangeDetails } from 'components/exchange-details';
import { RefundInfoBlock } from 'components/refund-info-block';
import { CheckoutShipment } from 'components/checkout-shipment';
import { SendReceiptDialog } from 'components/send-receipt-dialog';
import { joinRoutes, joinStrings, formatPrice } from 'utils/helpers';
import { VoidCheckoutDialog } from 'components/void-checkout-dialog';
import { RefundExchangeDrawer } from 'components/refund-exchange-drawer';
import { PrintReceiptFromTerminalButton } from 'components/print-receipt-from-terminal-button';
import {
  BadgeColor,
  ButtonColors,
  BadgePadding,
  RefundStatus,
  SendReceiptType,
  CloverReceiptType,
} from 'types';

import * as Styles from './styles';
import { ReceiptDetailsProps } from './types';
import {
  getPaymentMethodTypes,
  getCartDataFromCheckout,
} from './helpers';

const {
  Home,
  CreateShipping,
  Receipts: { HTML, GiftHTML },
} = AppRoutes.Products;

const template = '48px repeat(5, 1fr)';
const titles = ['', 'Item', 'Labels', 'Price', 'Quantity', 'Total'];

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Flex gap={8}>
    <Styles.InfoLabel>{label}</Styles.InfoLabel>
    <p>{value}</p>
  </Flex>
);

export const ReceiptDetails = ({
  data,
  cart,
  eventId,
  receiptId,
  hideButtons,
  onRefundOpen,
  hideCustomer,
  isAccounting,
  hideActionButtons,
  showShippingItems,
  hasPadding = true,
  hideCreateExchange,
}: ReceiptDetailsProps) => {
  const mobile = isMobile();
  const [isExchange, setIsExchange] = useState(false);
  const [refundOpened, setRefundOpened] = useState(false);
  const [isShippingOpen, setShippingOpen] = useState(false);
  const { data: tokenEcommerce, isLoading: isTokenLoading } =
    useTokenEcommerceQuery();

  const { deviceOptions, selectedDeviceId: deviceId } = useSelectedCloverDevice(
    data?.event
  );

  const isOtherExchangeItemInCart =
    cart?.exchangeId && Number(cart?.exchangeId) !== Number(receiptId);

  const hasChargeback = !!data?.chargeback?.id;
  const hasTokenEcommerce = !isTokenLoading && !!tokenEcommerce?.token;
  const isShowTokenError =
    !hasTokenEcommerce && !data?.isManualCreditPaymentEnabled;
  const isRefundDisabled =
    data?.refund?.some((refund) => refund.status === RefundStatus.Full) ||
    hasChargeback;

  const { isCredit } =
    getPaymentMethodTypes(data);

  const displayItems = showShippingItems
    ? data?.shippingItems || []
    : data?.items;

  const isVoided = data?.isVoided;
  const isActiveCheckout = !data?.isVoided && !data?.refund?.length;
  const isShipping = !!data?.shippingPrice && data?.shippingItems?.length;
  const isShowShipping = !isShipping && isActiveCheckout && !isRefundDisabled;
  const timezoneDate = dayjs(getReceiptDate(data)).tz(data?.event?.timezone?.code);
  const isShowVoidDialog =
    !data?.isManualCreditPaymentEnabled &&
    isShowShipping &&
    isCredit &&
    dayjs().diff(timezoneDate, 'minute') < 25;

  const sendReceipt = <SendReceiptDialog data={data} />;
  const shippingCartData = useMemo(() => getCartDataFromCheckout(data), [data]);
  const createShippingPath = useMemo(
    () =>
      joinRoutes(
        isAccounting
          ? [
              AppRoutes.Accounting.Home,
              AppRoutes.Accounting.CreateShipping.Home,
            ]
          : [Home, CreateShipping],
        isAccounting ? undefined : { id: String(data?.eventId) }
      ),
    [data?.eventId, isAccounting]
  );

  const manualCreditPaymentBadge = data?.isManualCreditPaymentEnabled && (
    <div>
      <Badge
        text="Manual Credit"
        padding={BadgePadding.Small}
        styleType={BadgeColor.TetrieryBlue}
      />
    </div>
  );

  if (!data) {
    return null;
  }

  const printReceiptButton = (
    <PrintReceiptFromTerminalButton
      deviceId={deviceId}
      checkoutId={data?.id}
      deviceOptions={deviceOptions}
      pdfUrl={isVoided ? data?.voidPdfUrl : data?.pdfUrl}
      type={isVoided ? CloverReceiptType.Void : CloverReceiptType.Payment}
    />
  );

  return (
    <>
      <Styles.Container hasPadding={hasPadding}>
        <InfoBlock
          title={`Receipt #${data?.id}`}
          rightSlot={
            isShowShipping ? (
              <Button
                text="Create Shipping"
                colorType={ButtonColors.LightBlue}
                onClick={() => setShippingOpen(true)}
              />
            ) : undefined
          }
        >
          <Flex column gap={40}>
            <Flex column gap={24}>
              <Flex column gap={8}>
                <InfoRow label="Event" value={data?.event?.name} />
                <InfoRow
                  label="Date"
                  value={formatDate(timezoneDate, 'L LT')}
                />
                {data?.event?.address && (
                  <InfoRow label="Address" value={data.event.address} />
                )}
                <InfoRow
                  label="Sales Rep"
                  value={joinStrings(
                    data?.commissions?.map((value) => value.user?.name),
                    ', '
                  )}
                />

                {data?.customer && !hideCustomer && (
                  <InfoRow
                    label="Customer"
                    value={data?.customer?.person?.name}
                  />
                )}

                <Flex gap={16}>
                  <Styles.InfoLabel>Payment methods</Styles.InfoLabel>
                  <ReceiptPaymentMethods checkout={data} />
                </Flex>
                {isCredit && manualCreditPaymentBadge}

                {!!data?.syncedCustomers?.length && (
                  <InfoRow
                    label="Customer from card"
                    value={joinStrings(
                      data?.syncedCustomers?.map(
                        (customer) => customer?.person?.name
                      ) || [],
                      ', '
                    )}
                  />
                )}

                {data?.isFreeShipping && (
                  <div>
                    <Badge
                      text="Free Shipping"
                      icon={<Icon.Delivery />}
                      styleType={BadgeColor.TetrieryBlue}
                    />
                  </div>
                )}
              </Flex>

              <List
                count={displayItems?.length}
                mainWrapperStyles={{ padding: 0 }}
                header={
                  <ListHeader
                    padding="8px"
                    titles={titles}
                    template={template}
                  />
                }
              >
                {displayItems?.map((item) => (
                  <ListCard
                    key={item.id}
                    headers={titles}
                    template={template}
                    values={[
                      <Avatar url={item?.product?.photoUrl} />,
                      <b>{item.product.name}</b>,
                      item?.isShipping ? (
                        <div>
                          <Badge
                            text="Shipping"
                            icon={<Icon.Delivery />}
                            styleType={BadgeColor.TetrieryBlue}
                          />
                        </div>
                      ) : null,
                      <b>{formatPrice(item.price)}</b>,
                      <b>{item.quantity}</b>,
                      <b>{formatPrice(item.price * item.quantity)}</b>,
                    ]}
                  />
                ))}
              </List>
            </Flex>

            <TotalCart
              data={data}
              tip={data?.tip}
              eventTax={data?.tax}
              paymentMethods={data.paymentMethods}
              companyProcessingFees={data?.processingFees}
              shippingPrice={Number(data?.shippingPrice || 0)}
              isProcessingFeeEnabled={data?.isProcessingFeeEnabled}
            />

            {isShowTokenError && (
              <ErrorMessage>
                It is impossible to make a refund because Clover Ecommerce is
                not configured. Please contact the company owner.
              </ErrorMessage>
            )}

            {!hideButtons &&
              (isVoided ? (
                <Flex gap={16}>
                  {sendReceipt}
                  {printReceiptButton}
                </Flex>
              ) : (
                <Flex gap={16} column={mobile} justifyContent="space-between">
                  <Flex gap={16} column={mobile} flexWrap="wrap">
                    {sendReceipt}

                    <SendReceiptDialog
                      data={data}
                      receiptType={SendReceiptType.Gift}
                    />

                    {printReceiptButton}

                    {data.giftPdfUrl && (
                      <PrintReceiptFromTerminalButton
                        isGiftReceipt
                        deviceId={deviceId}
                        checkoutId={data?.id}
                        pdfUrl={data.giftPdfUrl}
                        deviceOptions={deviceOptions}
                        buttonProps={{ text: 'Gift' }}
                        type={
                          isVoided
                            ? CloverReceiptType.Void
                            : CloverReceiptType.Payment
                        }
                      />
                    )}

                    {Config.isDebugHTMLEnabled && (
                      <>
                        <Button to={HTML} text="View HTML" />
                        <Button to={GiftHTML} text="View Gift" />
                      </>
                    )}
                  </Flex>

                  {!hideActionButtons && (
                    <Flex gap={16}>
                      {!hideCreateExchange && (
                        <div>
                          <Button
                            text="Exchange"
                            icon={<Icon.Transfer />}
                            colorType={ButtonColors.LightBlue}
                            disabled={
                              isOtherExchangeItemInCart || isRefundDisabled
                            }
                            onClick={() => {
                              setIsExchange(true);
                              setRefundOpened(true);
                            }}
                          />
                        </div>
                      )}

                      <div>
                        <Button
                          text="Refund"
                          icon={<Icon.Repeat />}
                          loading={isTokenLoading}
                          colorType={ButtonColors.LightRed}
                          disabled={isRefundDisabled || isShowTokenError}
                          onClick={() => {
                            setIsExchange(false);
                            onRefundOpen?.(true);
                            setRefundOpened(true);
                          }}
                        />
                      </div>

                      {isShowVoidDialog && (
                        <div>
                          <VoidCheckoutDialog checkout={data} />
                        </div>
                      )}
                    </Flex>
                  )}
                </Flex>
              ))}
          </Flex>
        </InfoBlock>

        <RefundInfoBlock data={data} />

        <ExchangeDetails data={data} />
      </Styles.Container>

      {(!hideButtons || !hideActionButtons) && (
        <RefundExchangeDrawer
          eventId={eventId}
          receiptId={receiptId}
          opened={refundOpened}
          isExchange={isExchange}
          setOpened={(opened) => {
            onRefundOpen?.(opened);
            setRefundOpened(opened);
          }}
        />
      )}

      {isShippingOpen && (
        <CheckoutShipment
          event={data?.event}
          checkoutId={data.id}
          withMaxProductQuantity
          opened={isShippingOpen}
          cartData={shippingCartData}
          navigateUrl={createShippingPath}
          setShipmentOpened={setShippingOpen}
        />
      )}
    </>
  );
};
